
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../utils/layout/DashboardLayout';
import {
  FaPlayCircle,
  FaCalendarAlt,
  FaClock,
  FaUser,
  FaVideo,
  FaArrowLeft,
  FaSpinner,
  FaExclamationCircle,
  FaExternalLinkAlt,
  FaFileVideo,
  FaUsers,
  FaHashtag,
  FaDownload,
  FaSearch,
  FaFilter,
  FaSort,
  FaEye,
  FaEyeSlash,
  FaInfoCircle,
  FaCloudDownloadAlt,
  FaRegCalendarCheck,
  FaCheckCircle,
  FaTimesCircle,
  FaBook,
  FaGraduationCap,
  FaChartLine,
  FaTimes,
  FaExpand,
  FaCompress,
  FaVolumeUp,
  FaVolumeMute,
  FaPlay,
  FaPause,
  FaStepForward,
  FaStepBackward,
  FaChevronLeft,
  FaChevronRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaTrashAlt
} from 'react-icons/fa';

const CourseRecordingsPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Recordings state
  const [recordings, setRecordings] = useState([]);
  const [loadingRecordings, setLoadingRecordings] = useState(false);
  
  // Filter & Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recordedAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [minDuration, setMinDuration] = useState('');
  const [maxDuration, setMaxDuration] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  
  // Pagination state (Now driven by Backend)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecordings, setTotalRecordings] = useState(0);
  
  // Selected recording
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [togglingVisibility, setTogglingVisibility] = useState(null);
  const [deletingRecording, setDeletingRecording] = useState(null);
  
  // Video Player state
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');
  const [currentVideoTitle, setCurrentVideoTitle] = useState('');
  const videoRef = useRef(null);
  
  // Stats (Calculated for current page)
  const [stats, setStats] = useState({
    totalRecordings: 0,
    totalDuration: 0,
    totalSize: 0,
    avgDuration: 0
  });

  // Fetch course details
  useEffect(() => {
    const fetchCourseDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}/course/getSingleCourse/${courseId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          setCourse(response.data.data);
        } else {
          setError('Failed to fetch course details');
        }
      } catch (err) {
        console.error('Error fetching course:', err);
        setError(err.response?.data?.message || 'Failed to load course details');
      } finally {
        setLoading(false);
      }
    };

    if (courseId && token) {
      fetchCourseDetails();
    }
  }, [courseId, token]);

  // Fetch recordings with Search, Filter & Pagination from Backend
  const fetchCourseRecordings = async () => {
    if (!courseId || !token) return;

    try {
      setLoadingRecordings(true);
      
      const params = new URLSearchParams({
        courseId,
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder
      });

      if (searchTerm) params.append("searchTerm", searchTerm);
      if (minDuration) params.append("minDuration", minDuration);
      if (maxDuration) params.append("maxDuration", maxDuration);
      if (dateRange.from) params.append("startDate", dateRange.from);
      if (dateRange.to) params.append("endDate", dateRange.to);

      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/liveSession/search-recordings?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        const fetchedData = response.data.data || [];
        setRecordings(fetchedData);
        setTotalPages(response.data.pagination.totalPages || 1);
        setTotalRecordings(response.data.pagination.total || 0);
        calculateStats(fetchedData);
      } else {
        setRecordings([]);
        setTotalRecordings(0);
      }
    } catch (err) {
      console.error('Error fetching searched recordings:', err);
      setRecordings([]);
      setTotalRecordings(0);
    } finally {
      setLoadingRecordings(false);
    }
  };

  // Debounced Effect: Trigger backend search when filters or pagination change
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchCourseRecordings();
    }, 500); // 500ms delay to prevent excessive API calls while typing

    return () => clearTimeout(delayDebounceFn);
  }, [courseId, token, currentPage, itemsPerPage, searchTerm, sortBy, sortOrder, minDuration, maxDuration, dateRange]);

  // Reset to first page when search/filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, sortOrder, minDuration, maxDuration, dateRange]);


  // Toggle Visibility API
  const handleToggleVisibility = async (sessionId, recordingId, currentStatus) => {
    try {
      setTogglingVisibility(recordingId);
      const newStatus = !currentStatus;
      
      const response = await axios.patch(
        `${import.meta.env.VITE_API_URL}/liveSession/toggle-recording-visibility`,
        {
          sessionId: sessionId,
          recordingId: recordingId,
          isPublished: newStatus
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setRecordings(prev => prev.map(rec => 
          rec._id === recordingId ? { ...rec, isPublished: newStatus } : rec
        ));
      }
    } catch (err) {
      console.error("Error toggling visibility:", err);
      alert("Failed to update visibility.");
    } finally {
      setTogglingVisibility(null);
    }
  };

  // Delete Recording API
  const handleDeleteRecording = async (sessionId, recordingId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this recording? This will permanently remove it from the AWS server and database. This action cannot be undone."
    );

    if (!confirmDelete) return;

    try {
      setDeletingRecording(recordingId);
      
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/liveSession/delete-recording`,
        {
          sessionId: sessionId,
          recordingId: recordingId
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        alert("Recording deleted successfully.");
        fetchCourseRecordings(); // Refresh the list from backend to get correct pagination
      }
    } catch (err) {
      console.error("Error deleting recording:", err);
      alert(err.response?.data?.message || "Failed to delete recording. Please try again.");
    } finally {
      setDeletingRecording(null);
    }
  };

  // Handle download recording
  const handleDownloadRecording = async (sessionId, recordingId, fileName, fileUrl) => {
    try {
      setDownloading(true);
      setSelectedRecording(recordingId);
      
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName || 'recording.mp4';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (err) {
      console.error('Error downloading recording:', err);
      alert('Failed to download recording. Please try again.');
    } finally {
      setDownloading(false);
      setSelectedRecording(null);
    }
  };

  // Handle watch now
  const handleWatchNow = (recording) => {
    setCurrentVideoUrl(recording.fileUrl);
    setCurrentVideoTitle(recording.title || recording.sessionTitle || recording.fileName);
    setShowVideoPlayer(true);
  };

  // Calculate statistics (For current page data)
  const calculateStats = (recordingsData) => {
    const total = recordingsData.length;
    const totalDuration = recordingsData.reduce((sum, rec) => sum + (rec.duration || 0), 0);
    const totalSize = recordingsData.reduce((sum, rec) => sum + (rec.fileSize || 0), 0);
    const avgDuration = total > 0 ? totalDuration / total : 0;
    
    setStats({
      totalRecordings: total,
      totalDuration,
      totalSize,
      avgDuration
    });
  };

  // Formatting helpers
  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // Pagination Handlers
  const goToPage = (page) => setCurrentPage(Math.max(1, Math.min(page, totalPages)));

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pageNumbers.push(i);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pageNumbers.push(i);
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pageNumbers.push(i);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    return pageNumbers;
  };

  const resetFilters = () => {
    setSearchTerm(''); setMinDuration(''); setMaxDuration('');
    setDateRange({ from: '', to: '' }); setSortBy('recordedAt'); setSortOrder('desc');
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  // Calculate Display Indices for UI
  const startIndex = totalRecordings === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalRecordings);

  // Video Player Modal
  const VideoPlayerModal = () => {
    if (!showVideoPlayer) return null;
    return (
      <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
        <div className="relative w-full h-full max-w-6xl mx-auto">
          <button
            onClick={() => {
              setShowVideoPlayer(false);
              setCurrentVideoUrl('');
              setCurrentVideoTitle('');
            }}
            className="absolute top-4 right-4 z-50 text-white hover:text-gray-300 transition-colors bg-black/50 p-2 rounded-full"
          >
            <FaTimes className="h-6 w-6" />
          </button>
          <div className="w-full h-full flex flex-col">
            <div className="px-6 py-4 bg-gray-900 border-b border-gray-800">
              <h3 className="text-xl font-semibold text-white truncate">{currentVideoTitle}</h3>
            </div>
            <div className="flex-1 relative bg-black">
              <video
                ref={videoRef} src={currentVideoUrl} className="w-full h-full object-contain"
                controls autoPlay playsInline crossOrigin="anonymous"
              >
                <source src={currentVideoUrl} type="video/mp4" />
                <source src={currentVideoUrl} type="video/webm" />
              </video>
            </div>
            <div className="px-6 py-3 bg-gray-900 border-t border-gray-800">
              <div className="flex items-center justify-between text-sm text-gray-400">
                <span>Use browser controls to play/pause</span>
                <button
                  onClick={() => window.open(currentVideoUrl, '_blank')}
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Open in new tab
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <FaSpinner className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading course details...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !course) {
    return (
      <DashboardLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <FaExclamationCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Error</h2>
            <p className="text-gray-600 mb-6">{error || "Course Not Found"}</p>
            <button
              onClick={() => navigate('/courses')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Courses
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50">
        <VideoPlayerModal />

        {/* Header */}
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between py-6">
              <div className="flex items-center space-x-4 mb-4 md:mb-0">
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <FaArrowLeft className="h-5 w-5 mr-2" /> Back
                </button>
                <div className="h-8 w-px bg-gray-300 hidden md:block" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Manage Course Recordings</h1>
                  <p className="text-gray-600 text-sm mt-1 flex items-center">
                    <FaBook className="h-4 w-4 mr-2" />
                    {course.title} • Dashboard for Streamer
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          
          {/* Stats Cards (Currently showing page level stats) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 text-blue-600 rounded-lg mr-4"><FaFileVideo className="h-6 w-6" /></div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{totalRecordings}</div>
                  <div className="text-sm text-gray-600">Total Videos (All)</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 text-purple-600 rounded-lg mr-4"><FaCloudDownloadAlt className="h-6 w-6" /></div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{formatFileSize(stats.totalSize)}</div>
                  <div className="text-sm text-gray-600">Storage (Current Page)</div>
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filter Bar */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6 mb-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <FaSearch className="h-5 w-5 mr-2 text-blue-600" /> Filter Library
                </h3>
              </div>
              <div className="mt-4 md:mt-0">
                <button
                  onClick={resetFilters}
                  className="text-sm text-gray-600 hover:text-gray-900 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
                >
                  <FaFilter className="h-4 w-4 mr-2" /> Reset All Filters
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text" placeholder="Search title..." value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                <div className="flex space-x-2">
                  <select
                    value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="recordedAt">Date Recorded</option>
                    <option value="duration">Duration</option>
                    <option value="title">Title</option>
                  </select>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                  >
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Recordings Grid */}
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex flex-col md:flex-row md:items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <FaFileVideo className="h-5 w-5 mr-2 text-purple-600" /> Available Recordings
                </h3>
                <p className="text-sm text-gray-500 mt-1">Showing {startIndex} - {endIndex} of {totalRecordings} results</p>
              </div>
              <div className="mt-2 md:mt-0 flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Show:</span>
                  <select
                    value={itemsPerPage} onChange={handleItemsPerPageChange}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm outline-none"
                  >
                    <option value={6}>6</option>
                    <option value={9}>9</option>
                    <option value={18}>18</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              {loadingRecordings ? (
                <div className="text-center py-12">
                  <FaSpinner className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
                  <p className="text-gray-600">Searching recordings...</p>
                </div>
              ) : recordings.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {recordings.map((recording) => (
                      <div key={recording._id} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md">
                        
                        {/* Thumbnail */}
                        <div 
                          className="aspect-video bg-gray-800 relative cursor-pointer group"
                          onClick={() => handleWatchNow(recording)}
                        >
                          <div className="absolute inset-0 flex items-center justify-center">
                            <FaPlayCircle className="h-14 w-14 text-white opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" />
                          </div>
                          {/* Visibility Badge */}
                          <div className="absolute top-2 left-2">
                            {recording.isPublished ? (
                              <span className="bg-green-500/90 text-white text-xs px-2 py-1 rounded-md flex items-center font-semibold shadow">
                                <FaEye className="mr-1" /> Public
                              </span>
                            ) : (
                              <span className="bg-red-500/90 text-white text-xs px-2 py-1 rounded-md flex items-center font-semibold shadow">
                                <FaEyeSlash className="mr-1" /> Private
                              </span>
                            )}
                          </div>
                          
                          <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                            {formatDuration(recording.duration)}
                          </div>
                        </div>
                        
                        {/* Info Body */}
                        <div className="p-4">
                          <h4 className="font-semibold text-gray-900 text-sm mb-2 line-clamp-2" title={recording.sessionTitle || recording.fileName}>
                            {recording.sessionTitle || recording.fileName}
                          </h4>
                          
                          <div className="space-y-1 mb-3 text-xs text-gray-500">
                            <div className="flex items-center"><FaCalendarAlt className="h-3 w-3 mr-2 text-blue-500" /> {formatDateTime(recording.recordedAt)}</div>
                            <div className="flex items-center"><FaCloudDownloadAlt className="h-3 w-3 mr-2 text-purple-500" /> {formatFileSize(recording.fileSize)}</div>
                          </div>
                          
                          {/* Visibility Toggle Control */}
                          <div className="flex items-center justify-between border-t border-gray-100 pt-3 pb-3">
                            <span className="text-sm font-medium text-gray-700">Visibility Status</span>
                            <button
                              onClick={() => handleToggleVisibility(recording.sessionId, recording._id, recording.isPublished)}
                              disabled={togglingVisibility === recording._id}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${recording.isPublished ? 'bg-green-500' : 'bg-gray-300'}`}
                            >
                              <span className={`pointer-events-none flex items-center justify-center h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${recording.isPublished ? 'translate-x-5' : 'translate-x-0'}`}>
                                {togglingVisibility === recording._id ? <FaSpinner className="animate-spin text-gray-400 text-[10px]" /> : null}
                              </span>
                            </button>
                          </div>

                          {/* Delete, Download and Play Buttons */}
                          <div className="flex space-x-2 pt-2 border-t border-gray-100">
                            <button
                              onClick={() => handleDeleteRecording(recording.sessionId, recording._id)}
                              disabled={deletingRecording === recording._id}
                              className="flex-1 justify-center inline-flex items-center bg-red-50 text-red-600 px-3 py-2 rounded text-sm hover:bg-red-100 transition-colors"
                              title="Delete Recording"
                            >
                              {deletingRecording === recording._id ? <FaSpinner className="animate-spin" /> : <FaTrashAlt />}
                            </button>
                            <button
                              onClick={() => handleDownloadRecording(recording.sessionId, recording._id, recording.fileName, recording.fileUrl)}
                              disabled={downloading && selectedRecording === recording._id}
                              className="flex-1 justify-center inline-flex items-center bg-gray-100 text-gray-700 px-3 py-2 rounded text-sm hover:bg-gray-200 transition-colors"
                              title="Download Video"
                            >
                              {downloading && selectedRecording === recording._id ? <FaSpinner className="animate-spin" /> : <FaDownload />}
                            </button>
                            <button
                              onClick={() => handleWatchNow(recording)}
                              className="flex-[2] justify-center inline-flex items-center bg-blue-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-blue-700 transition-colors"
                            >
                              Play Video
                            </button>
                          </div>
                        </div>

                      </div>
                    ))}
                  </div>

                  {/* Backend Driven Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-8 flex justify-center border-t border-gray-200 pt-6">
                       <div className="flex space-x-2">
                        {getPageNumbers().map((page, idx) => (
                           page === '...' ? <span key={idx} className="px-3 py-2">...</span> :
                           <button key={page} onClick={() => goToPage(page)} className={`px-4 py-2 rounded-lg text-sm font-medium ${currentPage === page ? 'bg-blue-600 text-white' : 'border border-gray-300 bg-white hover:bg-gray-50'}`}>
                             {page}
                           </button>
                        ))}
                       </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <FaFileVideo className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h4 className="font-medium text-gray-900 mb-2">No Recordings Found</h4>
                  <p className="text-gray-500 mb-6">There are no recordings matching your search filters.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CourseRecordingsPage;